import * as sha from 'js-sha256';
import {analyseCondition, ConditionNode, formatConditionDate} from '../../workflow-condition';
import {DateKind, ValueType} from '../types';

/** Describes a workflow variable using its source label without changing its stored identifier. */
export interface ConditionVariable {
  name: string;
  label: string;
  detail: string;
  group: 'survey' | 'questionnaire';
  valueType: ValueType;
  dateKind?: DateKind;
  choices?: {value: string; label: string}[];
}

/** Defines the editable group shape with a single logical operator for all its children. */
export type ConditionGroup = Extract<ConditionNode, {kind: 'group'}>;
/** Defines a single comparison without changing the stored literal type. */
export type ConditionRule = Extract<ConditionNode, {kind: 'rule'}>;
/** Describes one safe text fragment in the highlighted preview. */
export interface ConditionPart {kind: 'source' | 'text' | 'logic'; text: string; title?: string;}

export const COMPARISONS = {
  '==': 'ist gleich', '!=': 'ist ungleich', '>': 'ist größer als', '<': 'ist kleiner als',
  '>=': 'ist mindestens', '<=': 'ist höchstens'
};

/** Creates a detached empty condition that represents an unconditional step. */
export function emptyGroup(): ConditionGroup { return {kind: 'group', operator: '&&', children: []}; }

/** Creates a rule that requires an explicit variable selection. */
export function emptyRule(): ConditionRule { return {kind: 'rule', variable: '', operator: '==', value: ''}; }

/** Reads existing server expressions without executing them or discarding unsupported source. */
export function readCondition(expression: string): ConditionGroup | null {
  const result = analyseCondition(expression);
  if (result.error) { return null; }
  if (!result.root) { return emptyGroup(); }
  return result.root.kind === 'group' ? result.root : {...emptyGroup(), children: [result.root]};
}

/** Generates parenthesized JavaScript comparisons with safely quoted literal values. */
export function compileCondition(node: ConditionNode): string {
  if (node.kind === 'group') {
    if (!node.children.length) { return ''; }
    return '(' + node.children.map(compileCondition).join(` ${node.operator} `) + ')';
  }
  return `${node.variable} ${node.operator} ${JSON.stringify(node.value)}`;
}

/** Keeps the literal type of existing rules instead of coercing legacy string or numeric comparisons. */
export function valueType(rule: ConditionRule, variables: ConditionVariable[]): ValueType {
  const source = variables.find(variable => variable.name === rule.variable);
  if (source?.valueType === 'date' && typeof rule.value === 'string') { return 'date'; }
  if (typeof rule.value === 'boolean') { return 'boolean'; }
  if (typeof rule.value === 'number' || rule.value === null) { return 'number'; }
  return 'string';
}

/** Checks one comparison using the syntax and variable scope supported by the workflow server. */
export function ruleIssue(rule: ConditionRule, variables: ConditionVariable[]): string {
  const source = variables.find(variable => variable.name === rule.variable);
  if (!rule.variable) { return 'Bitte wählen Sie eine Variable.'; }
  if (!source) { return 'Diese Workflow-Variable existiert nicht mehr. Bitte ersetzen Sie sie.'; }
  if (!/^[A-Za-z_$][A-Za-z0-9_$]*$/.test(rule.variable) || !COMPARISONS[rule.operator]) { return 'Dieser Vergleich wird nicht unterstützt.'; }
  if (rule.value === null || rule.value === undefined || (typeof rule.value === 'number' && !Number.isFinite(rule.value))) {
    return 'Bitte geben Sie einen gültigen Vergleichswert ein.';
  }
  if (valueType(rule, variables) === 'date') {
    const value = String(rule.value);
    const kind = source.dateKind || 'fullDate';
    const pattern = kind === 'yearDate' ? /^\d{4}$/ : kind === 'monthDate' ? /^\d{4}-(0[1-9]|1[0-2])$/ :
      kind === 'fullDateTime' ? /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:\d{2})?$/ :
      /^\d{4}-\d{2}-\d{2}$/;
    if (!pattern.test(value) || !Number.isFinite(Date.parse(value))) { return 'Bitte geben Sie ein gültiges Datum ein.'; }
    if (value.length >= 10 && new Date(value.slice(0, 10)).toISOString().slice(0, 10) !== value.slice(0, 10)) {
      return 'Bitte geben Sie ein gültiges Datum ein.';
    }
  }
  return analyseCondition('(' + compileCondition(rule) + ')').error ? 'Dieser Vergleich wird nicht unterstützt.' : '';
}

/** Rejects incomplete groups while allowing the root to be deliberately cleared. */
export function conditionIssue(group: ConditionGroup, variables: ConditionVariable[]): string {
  if (!group) { return 'Dieser Ausdruck kann nicht im visuellen Editor bearbeitet werden. Er bleibt unverändert.'; }
  if (!['&&', '||'].includes(group.operator)) { return 'Dieser Vergleich wird nicht unterstützt.'; }
  for (const child of group.children) {
    const issue = child.kind === 'rule' ? ruleIssue(child, variables) : !child.children.length ?
      'Bitte ergänzen oder entfernen Sie die leere Gruppe.' : conditionIssue(child, variables);
    if (issue) { return issue; }
  }
  return '';
}

/** Produces safe text fragments for source chips and explicitly grouped summaries. */
export function conditionParts(node: ConditionNode, variables: ConditionVariable[], translate: (text: string) => string,
  dateFormat: (token: string) => string = token => token): ConditionPart[] {
  if (!node) { return [{kind: 'text', text: translate('Vorhandener Ausdruck')}]; }
  if (node.kind === 'group') {
    if (!node.children.length) { return [{kind: 'text', text: translate('Dieser Schritt wird immer ausgeführt.')}]; }
    const parts: ConditionPart[] = [];
    node.children.forEach((child, index) => {
      if (index) { parts.push({kind: 'logic', text: ` ${translate(node.operator === '&&' ? 'UND' : 'ODER')} `}); }
      if (child.kind === 'group') { parts.push({kind: 'logic', text: '('}); }
      parts.push(...conditionParts(child, variables, translate, dateFormat));
      if (child.kind === 'group') { parts.push({kind: 'logic', text: ')'}); }
    });
    return parts;
  }
  const source = variables.find(variable => variable.name === node.variable);
  const label = source?.label || node.variable || translate('Variable wählen');
  const choice = source?.choices?.find(option => option.value === node.value);
  const date = source?.valueType === 'date' ?
    formatConditionDate(node.value, {target: {options: {format: 'date'}}}, dateFormat) : null;
  const value = date || choice?.label || (typeof node.value === 'boolean' ? translate(node.value ? 'Ja' : 'Nein') :
    typeof node.value === 'string' ? `„${node.value}“` : String(node.value ?? '…'));
  return [{kind: 'source', text: label, title: [label, node.variable, source?.detail].filter(Boolean).join('\n')},
    {kind: 'text', text: ` ${translate(COMPARISONS[node.operator] || node.operator)} ${value}`}];
}

/** Extracts plain source labels without allowing authored markup into editor controls. */
function plain(value: string): string {
  const document = new DOMParser().parseFromString(value || '', 'text/html');
  return (document.body.textContent || '').replace(/\s+/g, ' ').trim();
}

/** Resolves readable labels and formats from current workflow mappings and their questionnaire sources. */
export function buildConditionVariables(workflow: any, questionnaires: any[], customFields: any[], locale: string,
  translate: (text: string) => string): ConditionVariable[] {
  const labels = {age: 'Alter in Jahren', ageInMonths: 'Alter in Monaten', birthDate: 'Geburtsdatum', firstName: 'Vorname',
    lastName: 'Nachname', name: 'Vollständiger Name', isMale: 'Männlich', isFemale: 'Weiblich', isDiverse: 'Divers',
    anonymous: 'Anonymer Patient', pseudonymous: 'Pseudonymer Patient', caseNr: 'Fallnummer', customerNr: 'Patientennummer',
    zipCode: 'Postleitzahl', city: 'Ort', street: 'Straße', streetNr: 'Hausnummer', location: 'Standort', status: 'Status'};
  const indexed = new Map<string, Map<string, {label: string; format?: string; options?: any; dataType?: string}>>();
  /** Reads localized content titles without modifying the loaded questionnaire. */
  const title = (value: any): string => plain(value?.[locale] || value?.de_DE || '');
  (questionnaires || []).forEach(questionnaire => {
    const map = new Map();
    /** Mirrors the questionnaire path hash calculation while retaining answer hierarchy and inherited formats. */
    const walk = (element: any, path = '', parentLabel = '', inherited: any = {}): void => {
      const currentPath = element.omitContainer ? path : path ? `${path}.${element.id}` : element.id;
      const ownLabel = element.omitContainer ? '' : title(element.title);
      const label = [parentLabel, ownLabel].filter(Boolean).join(' › ');
      const format = element.format || inherited.format;
      const info = {label, format, dataType: element.dataType || inherited.dataType, options: {...inherited.options, ...element.options}};
      if (!element.omitContainer && currentPath) { map.set(element.pathHash || sha.sha256(currentPath), info); }
      (element.elements || []).forEach(child => walk(child, currentPath, label, info));
    };
    (questionnaire.questions || []).forEach(question => walk(question));
    indexed.set(questionnaire.id, map);
  });
  const variables = (workflow?.variables || []).filter(variable => variable.name).map(variable => {
    const target = variable.target || {};
    const [scope, name] = (target.variable || '').split('.');
    const questionnaire = questionnaires?.find(item => item.id === target.id);
    const mapping = questionnaire?.meta?.options?.variables?.[scope]?.[name];
    const hash = Object.keys(mapping || {})[0];
    const mapped = indexed.get(target.id)?.get(hash);
    const custom = target.type === 'survey' && customFields?.find(field => target.variable === `client.custom.${field.key}`);
    const options = {...mapped?.options, ...target.options};
    const patientFormat = ['age', 'ageInMonths', 'caseNr', 'customerNr', 'zipCode', 'streetNr'].includes(name) ? 'numeric' :
      ['isMale', 'isFemale', 'isDiverse', 'anonymous', 'pseudonymous'].includes(name) ? 'boolean' :
      name === 'birthDate' ? 'date' : 'text';
    const format = custom?.type || options.format || mapped?.format || (target.type === 'survey' ? patientFormat : 'text');
    const dataType = options.dataType || mapped?.dataType;
    const label = target.type === 'survey' ? custom?.label || translate(labels[name] || variable.name) :
      mapped?.label || variable.name;
    const type: ValueType = dataType === 'boolean' || ['boolean', 'singleChoice', 'multipleChoice', 'matrix'].includes(format) ? 'boolean' :
      dataType === 'number' || ['numeric', 'number'].includes(format) || ['scoring', 'formula'].includes(scope) ? 'number' :
      format === 'date' ? 'date' : 'string';
    return {name: variable.name, label, group: target.type === 'survey' ? 'survey' : 'questionnaire', valueType: type,
      detail: [title(questionnaire?.meta?.title), target.variable].filter(Boolean).join(' › '),
      dateKind: options.fullDateTime ? 'fullDateTime' : options.monthDate ? 'monthDate' : options.yearDate ? 'yearDate' : 'fullDate',
      choices: custom?.type === 'select' ? custom.options || [] : []} as ConditionVariable;
  });
  return variables.map(variable => variables.filter(item => item.label === variable.label).length > 1 ?
    {...variable, label: `${variable.label} (${variable.name})`} : variable);
}
